import axios from 'axios';

// Determine API base URL - works for both local development and production
const getApiBaseUrl = () => {
  // For local development, use the backend port at root API level
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000/api/v1';
  }
  
  // For production, assume API is served from same host but different port or path
  return '/api/v1';  // Root API level
};

// Determine legal domain base URL for legal-specific endpoints
const getLegalApiBaseUrl = () => {
  // For local development, use the backend port with legal domain
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000/api/v1/legal';
  }
  
  // For production, assume API is served from same host but different port or path
  return '/api/v1/legal';  // Legal domain prefix
};

const API_BASE_URL = getApiBaseUrl();
const LEGAL_API_BASE_URL = getLegalApiBaseUrl();

// Export the base URL functions for use in other components
export { getApiBaseUrl, getLegalApiBaseUrl };

// Main API instance for auth and general endpoints
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Legal API instance for legal-specific endpoints
const legalApi = axios.create({
  baseURL: LEGAL_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Legal Policy Management Endpoints
export const uploadLegalPolicy = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await legalApi.post('/policies/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const searchLegalPolicies = async (query = '', category = '', source = '', limit = 20, offset = 0) => {
  const response = await legalApi.get('/policies/search', {
    params: { query, category, source, limit, offset },
  });
  return response.data;
};

export const getLegalPolicySources = async () => {
  const response = await legalApi.get('/policies/sources');
  return response.data;
};

export const deleteLegalPolicy = async (policyId) => {
  const response = await legalApi.delete(`/policies/${policyId}`);
  return response.data;
};

export const deleteLegalPoliciesBulk = async (policyIds) => {
  const response = await legalApi.delete('/policies/bulk', { data: policyIds });
  return response.data;
};

export const deleteLegalPoliciesBySource = async (sourceFile) => {
  const response = await legalApi.delete(`/policies/source/${sourceFile}`);
  return response.data;
};

// Legal Discovery Processing
export const processLegalDiscovery = async (text, useRules = true) => {
  const response = await legalApi.post('/discovery/process', { 
    text, 
    use_rules: useRules 
  });
  return response.data;
};

// Document Classification - use legalApi for legal domain
export const classifyDocument = async (text, useScgRules = false) => {
  const endpoint = '/classify';
  const response = await legalApi.post(endpoint, { text, use_legal_policies: true });
  return response.data;
};

// Enhanced Document Classification with advanced retrieval - use legalApi for legal domain
export const classifyDocumentEnhanced = async (text, options = {}) => {
  const {
    useScgRules = false,
    useHyde = true,
    useSemanticRetrieval = true
  } = options;
  
  const response = await legalApi.post('/classify', { 
    text, 
    use_scg_rules: useScgRules,
    use_hyde: useHyde,
    use_semantic_retrieval: useSemanticRetrieval
  });
  return response.data;
};

// Paragraph-level Classification - use legalApi for legal domain
export const classifyParagraphs = async (text, options = {}) => {
  const {
    chunkSize = 1000,
    chunkOverlap = 100,
    useScgRules = false
  } = options;
  
  const response = await legalApi.post('/classify/paragraphs', {
    text,
    chunk_size: chunkSize,
    chunk_overlap: chunkOverlap,
    use_scg_rules: useScgRules
  });
  return response.data;
};

// Document Upload
export const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Upload and Classify Document with Portion Marks
export const uploadAndClassifyParagraphs = async (file, options = {}) => {
  const {
    useScgRules = true,
    chunkSize = 1500,
    useLlmSegmentation = true
  } = options;
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('use_scg_rules', useScgRules.toString());
  formData.append('chunk_size', chunkSize.toString());
  formData.append('use_llm_segmentation', useLlmSegmentation.toString());
  
  const response = await api.post('/upload/classify-paragraphs', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Upload and generate formatted document with portion marks
export const uploadAndClassifyFormatted = async (file, options = {}) => {
  const {
    useScgRules = true,
    chunkSize = 1500
  } = options;
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('use_scg_rules', useScgRules.toString());
  formData.append('chunk_size', chunkSize.toString());
  
  const response = await api.post('/upload/classify-formatted', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob', // Important for binary file downloads
  });
  return response;
};

// Format document using existing classification results (no re-classification)
export const formatClassifiedDocument = async (file, classificationData) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('classification_data', JSON.stringify(classificationData));
  
  const response = await api.post('/format-document', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob', // Important for binary file downloads
  });
  return response;
};

// SCG Management
export const uploadSCGDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/scg/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getSCGRules = async () => {
  const response = await api.get('/scg/rules');
  return response.data;
};

// SCG Rule Management
export const searchSCGRules = async (query = "", classificationLevel = "", source = "", limit = 50, offset = 0) => {
  const params = new URLSearchParams();
  if (query) params.append('query', query);
  if (classificationLevel) params.append('classification_level', classificationLevel);
  if (source) params.append('source', source);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  
  const response = await api.get(`/scg/search?${params.toString()}`);
  return response.data;
};

export const getSCGSources = async () => {
  const response = await api.get('/scg/sources');
  return response.data;
};

export const getSCGRule = async (ruleId) => {
  const response = await api.get(`/scg/rule/${ruleId}`);
  return response.data;
};

export const updateSCGRule = async (ruleId, updateData) => {
  const response = await api.put(`/scg/rule/${ruleId}`, updateData);
  return response.data;
};

export const deleteSCGRule = async (ruleId) => {
  const response = await api.delete(`/scg/rule/${ruleId}`);
  return response.data;
};

export const deleteSCGRulesBulk = async (ruleIds) => {
  const response = await api.post('/scg/delete/bulk', { rule_ids: ruleIds });
  return response.data;
};

export const deleteSCGRulesBySource = async (sourceFile) => {
  const response = await api.delete(`/scg/source/${encodeURIComponent(sourceFile)}`);
  return response.data;
};

// SCG Source Information
export const getSCGRuleSourceInfo = async (ruleId) => {
  const response = await api.get(`/scg/rule/${ruleId}/source`);
  return response.data;
};

export const getSCGPdfUrl = (fileHash, page = null) => {
  // For Docker/production, use relative URL to avoid localhost port issues
  // For development, use the full localhost:8000 URL
  let baseUrl;
  if (process.env.NODE_ENV === 'development') {
    baseUrl = `${API_BASE_URL}/scg/pdf/${fileHash}`;
  } else {
    // Use relative URL for Docker/production to work with nginx proxy
    baseUrl = `/api/v1/scg/pdf/${fileHash}`;
  }
  return page ? `${baseUrl}?page=${page}` : baseUrl;
};

// Document Search
export const searchDocuments = async (query, topK = 10, filters = null, rewriteQuery = false) => {
  const response = await api.post('/search', {
    query,
    top_k: topK,
    filters,
    rewrite_query: rewriteQuery
  });
  return response.data;
};

// List Documents
export const listDocuments = async (limit = 50) => {
  const response = await api.get(`/documents?limit=${limit}`);
  return response.data;
};

// Generate Embedding
export const generateEmbedding = async (text) => {
  const response = await api.post('/embed', { text });
  return response.data;
};

// Query Rewriting
export const rewriteQuery = async (query) => {
  const response = await api.post(`/query/rewrite?query=${encodeURIComponent(query)}`);
  return response.data;
};

// Health Check
export const healthCheck = async () => {
  const response = await axios.get('/health'); // Use direct call, not through api instance
  return response.data;
};

// Tearline Document Sanitization
export const sanitizeDocument = async (text, targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, preClassifiedDocument = null) => {
  const response = await api.post('/tearline/sanitize', {
    text,
    target_classification: targetClassification,
    target_caveats: targetCaveats,
    target_releasability: targetReleasability,
    max_iterations: maxIterations,
    sanitization_methods: ["generalization", "redaction", "obfuscation", "paraphrasing", "abstraction", "temporal_shifting", "geographical_masking", "entity_anonymization", "technical_simplification"],
    pre_classified_document: preClassifiedDocument
  });
  return response.data;
};

// Streaming Tearline Document Sanitization
export const sanitizeDocumentStream = async (text, targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, onProgress, preClassifiedDocument = null) => {
  console.log('API: Starting streaming sanitization request...');
  console.log('API: onProgress callback provided:', typeof onProgress);
  console.log('API: Pre-classified document provided:', preClassifiedDocument ? 'Yes' : 'No');
  
  return new Promise((resolve, reject) => {
    // R2-AUTH: tearline routes now require the tearline_sanitize permission
    const tearlineToken = localStorage.getItem('authToken');
    // First, start the sanitization process
    fetch(`${API_BASE_URL}/tearline/sanitize/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(tearlineToken ? { Authorization: `Bearer ${tearlineToken}` } : {}),
      },
      body: JSON.stringify({
        text,
        target_classification: targetClassification,
        target_caveats: targetCaveats,
        target_releasability: targetReleasability,
        max_iterations: maxIterations,
        sanitization_methods: ["generalization", "redaction", "obfuscation", "paraphrasing", "abstraction", "temporal_shifting", "geographical_masking", "entity_anonymization", "technical_simplification"],
        pre_classified_document: preClassifiedDocument
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log('API: Got streaming response, starting to read...');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;

      function readStream() {
        return reader.read().then(({ done, value }) => {
          if (done) {
            console.log('API: Stream finished reading');
            resolve(finalResult);
            return;
          }
          
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log('API: Received streaming update:', data);
                
                // Call progress callback with update
                if (onProgress) {
                  console.log('API: Calling onProgress callback with:', data);
                  setTimeout(() => {
                    try {
                      onProgress(data);
                      console.log('API: onProgress callback completed successfully');
                    } catch (error) {
                      console.error('API: Error in onProgress callback:', error);
                    }
                  }, 0); // Use setTimeout to ensure async execution
                } else {
                  console.log('API: No onProgress callback available');
                }
                
                // Store final result
                if (data.final_result) {
                  console.log('API: Setting final result:', data.final_result);
                  finalResult = data.final_result;
                }
              } catch (e) {
                console.warn('API: Failed to parse streaming data:', line);
              }
            }
          }
          
          // Continue reading
          return readStream();
        });
      }
      
      return readStream();
    })
    .catch(error => {
      console.error('API: Streaming request failed:', error);
      reject(error);
    });
  });
};

// Testing and Optimization APIs
export const startTestRun = async (testType = 'classification', testQueries = null, configParams = null) => {
  const response = await api.post('/testing/run', {
    test_type: testType,
    test_queries: testQueries,
    config_params: configParams
  });
  return response.data;
};

export const getTestRunStatus = async (testId) => {
  const response = await api.get(`/testing/run/${testId}`);
  return response.data;
};

export const startConfigOptimization = async (optimizationParams, testQueries = null, scoringWeights = null) => {
  const response = await api.post('/testing/optimize', {
    test_queries: testQueries,
    optimization_params: optimizationParams,
    scoring_weights: scoringWeights
  });
  return response.data;
};

export const getOptimizationStatus = async (optimizationId) => {
  const response = await api.get(`/testing/optimize/${optimizationId}`);
  return response.data;
};

export const getDefaultTestQueries = async () => {
  const response = await api.get('/testing/default-queries');
  return response.data;
};

export const getConfigOptimizationTemplates = async () => {
  const response = await api.get('/testing/config-templates');
  return response.data;
};

// ==================== FOIA PROCESSING APIs ====================

// FOIA Document Processing
export const processFOIADocument = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// FOIA File Processing
export const processFOIAFile = async (file, agency = '', requesterInterest = '', useHyde = true) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  formData.append('use_hyde', useHyde);
  
  const response = await api.post('/foia/process/file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// FOIA Rule Management
export const uploadFOIAGuidance = async (file, agency = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  
  const response = await api.post('/foia/rules/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const searchFOIARules = async (query = '', exemptionCategory = '', source = '', limit = 50, offset = 0) => {
  const params = new URLSearchParams({
    query,
    exemption_category: exemptionCategory,
    source,
    limit: limit.toString(),
    offset: offset.toString()
  });
  
  const response = await api.post('/foia/rules/search', {
    query,
    exemption_category: exemptionCategory,
    source,
    limit,
    offset
  });
  return response.data;
};

export const getFOIASources = async () => {
  const response = await api.get('/foia/rules/sources');
  return response.data;
};

export const getFOIAExemptions = async () => {
  const response = await api.get('/foia/rules/exemptions');
  return response.data;
};

export const getFOIARule = async (ruleId) => {
  const response = await api.get(`/foia/rules/${ruleId}`);
  return response.data;
};

export const deleteFOIARule = async (ruleId) => {
  const response = await api.delete(`/foia/rules/${ruleId}`);
  return response.data;
};

export const deleteFOIARulesBulk = async (ruleIds) => {
  const response = await api.post('/foia/rules/delete/bulk', {
    rule_ids: ruleIds
  });
  return response.data;
};

export const deleteFOIARulesBySource = async (sourceFile) => {
  const response = await api.delete(`/foia/rules/source/${encodeURIComponent(sourceFile)}`);
  return response.data;
};

// Enhanced FOIA Processing (Standard Exemptions + Rule-based + Conflict Detection)
export const processEnhancedFOIA = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process/enhanced', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// Enhanced FOIA Processing with Streaming
export const processEnhancedFOIAStream = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process/enhanced/stream', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// PDF FOIA Redaction - Returns visually redacted PDF with full analysis
export const redactPDFForFOIA = async (file, agency = '', requesterInterest = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  
  const response = await api.post('/foia/redact/pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  });
  
  // Convert base64 PDF data to blob for download
  const pdfData = response.data.pdfData;
  const binaryString = atob(pdfData);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
  
  return {
    pdfBlob: pdfBlob,
    filename: response.data.filename,
    redactionCount: response.data.redactionCount,
    exemptionsUsed: response.data.exemptionsUsed,
    processingTime: response.data.processingTime,
    confidenceScore: response.data.confidenceScore,
    mappingStats: response.data.mappingStats,
    textAnalysis: response.data.textAnalysis  // Full analysis data
  };
};

// PDF FOIA Analysis - Returns detailed analysis only
export const analyzePDFForFOIA = async (file, agency = '', requesterInterest = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  
  const response = await api.post('/foia/analyze/pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

// Model Management
export const getAvailableModels = async () => {
  const response = await api.get('/config/models/available');
  return response.data;
};

export const getCurrentModel = async () => {
  const response = await api.get('/config/models/current');
  return response.data;
};

export const validateModelConfig = async (provider, modelId) => {
  const response = await api.post('/config/models/validate', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const switchModel = async (provider, modelId) => {
  const response = await api.post('/config/models/switch', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const clearModelCache = async () => {
  const response = await api.post('/config/models/cache/clear');
  return response.data;
};

// ==================== LiteLLM Model Configuration APIs ====================

/**
 * Get all available LLM providers
 * @returns {Promise<string[]>} List of provider names
 */
export const getLLMProviders = async () => {
  const response = await api.get('/models/providers');
  return response.data;
};

/**
 * Get available models with full metadata
 * @param {string|null} provider - Optional provider to filter by
 * @returns {Promise<Object>} Dictionary of provider -> model info array
 */
export const getLLMModelsWithInfo = async (provider = null) => {
  const params = provider ? { provider } : {};
  const response = await api.get('/models/available', { params });
  return response.data;
};

/**
 * Get available models (simple list of IDs)
 * @param {string|null} provider - Optional provider to filter by
 * @returns {Promise<Object>} Dictionary of provider -> model ID array
 */
export const getLLMModelsSimple = async (provider = null) => {
  const params = provider ? { provider } : {};
  const response = await api.get('/models/available/simple', { params });
  return response.data;
};

/**
 * Get all function model configurations
 * @returns {Promise<Object>} Dictionary of function_name -> config
 */
export const getFunctionConfigs = async () => {
  const response = await api.get('/models/configs');
  return response.data;
};

/**
 * Get model configuration for a specific function
 * @param {string} functionName - Name of the function
 * @returns {Promise<Object>} Function configuration
 */
export const getFunctionConfig = async (functionName) => {
  const response = await api.get(`/models/configs/${functionName}`);
  return response.data;
};

/**
 * Update model configuration for a specific function
 * @param {string} functionName - Name of the function
 * @param {Object} config - New configuration
 * @returns {Promise<Object>} Updated configuration
 */
export const updateFunctionConfig = async (functionName, config) => {
  const response = await api.put(`/models/configs/${functionName}`, config);
  return response.data;
};

/**
 * Delete a custom function configuration (reset to default)
 * @param {string} functionName - Name of the function
 * @returns {Promise<Object>} Success message
 */
export const deleteFunctionConfig = async (functionName) => {
  const response = await api.delete(`/models/configs/${functionName}`);
  return response.data;
};

/**
 * Reset all function configurations to defaults
 * @returns {Promise<Object>} Success message with reset configs
 */
export const resetAllFunctionConfigs = async () => {
  const response = await api.post('/models/configs/reset');
  return response.data;
};

/**
 * Test a model configuration
 * @param {string} modelId - Full model ID with provider prefix
 * @param {string} testPrompt - Optional test prompt
 * @param {number} temperature - Optional temperature
 * @param {number} maxTokens - Optional max tokens
 * @returns {Promise<Object>} Test results
 */
export const testModelConfig = async (modelId, testPrompt = 'Hello, please respond with a brief greeting.', temperature = 0.7, maxTokens = 100) => {
  const response = await api.post('/models/test', {
    model_id: modelId,
    test_prompt: testPrompt,
    temperature,
    max_tokens: maxTokens
  });
  return response.data;
};

/**
 * Get current model information from model manager
 * @returns {Promise<Object>} Current model info
 */
export const getCurrentModelInfo = async () => {
  const response = await api.get('/models/current');
  return response.data;
};

/**
 * Switch the current model at runtime
 * @param {string} provider - Provider name
 * @param {string} modelId - Model identifier
 * @returns {Promise<Object>} Success status and new model info
 */
export const switchLLMModel = async (provider, modelId) => {
  const response = await api.post('/models/switch', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

/**
 * Get list of functions that support model configuration
 * @returns {Promise<Array>} List of function info objects
 */
export const getSupportedFunctions = async () => {
  const response = await api.get('/models/functions');
  return response.data;
};

/**
 * Clear the model client cache
 * @returns {Promise<Object>} Success message
 */
export const clearLLMModelCache = async () => {
  const response = await api.post('/models/cache/clear');
  return response.data;
};

// Continue Tearline Sanitization Stream  
export const continueSanitizeDocumentStream = async (targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, previousResult, onProgress) => {
  console.log('API: Starting continue streaming sanitization request...');
  console.log('API: onProgress callback provided:', typeof onProgress);
  
  return new Promise((resolve, reject) => {
    // R2-AUTH: tearline routes now require the tearline_sanitize permission
    const tearlineToken = localStorage.getItem('authToken');
    // Start the continue sanitization process
    fetch(`${API_BASE_URL}/tearline/continue/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(tearlineToken ? { Authorization: `Bearer ${tearlineToken}` } : {}),
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({
        target_classification: targetClassification,
        target_caveats: targetCaveats,
        target_releasability: targetReleasability,
        max_iterations: maxIterations,
        previous_result: previousResult
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const readStream = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            console.log('API: Continue stream ended');
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          lines.forEach(line => {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log('API: Continue stream data:', data);

                if (onProgress) {
                  onProgress(data);
                }

                if (data.status === 'completed') {
                  console.log('API: Continue sanitization completed successfully');
                  resolve(data.final_result);
                } else if (data.status === 'error') {
                  console.error('API: Continue sanitization error:', data.error);
                  reject(new Error(data.message || data.error || 'Continue sanitization failed'));
                }
              } catch (parseError) {
                console.error('API: Failed to parse continue stream data:', parseError, line);
              }
            }
          });

          readStream();
        }).catch(error => {
          console.error('API: Continue stream read error:', error);
          reject(error);
        });
      };

      readStream();
    })
    .catch(error => {
      console.error('API: Continue fetch error:', error);
      reject(error);
    });
  });
};

// Process PDF for FOIA with initial redactions
export const processPDFForFOIA = async (pdfFile, agency = "", requesterInterest = "") => {
  const formData = new FormData();
  formData.append('file', pdfFile);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);

  const response = await api.post('/foia/process-pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  });
  
  return response.data;
};

// Generate redacted PDF with modified redactions
export const generateRedactedPDF = async (options) => {
  const {
    pdf_file,
    redactions = [],
    agency = "",
    requester_interest = ""
  } = options;

  const formData = new FormData();
  if (pdf_file) {
    formData.append('file', pdf_file);
  }
  formData.append('redactions', JSON.stringify(redactions));
  formData.append('agency', agency);
  formData.append('requester_interest', requester_interest);

  const response = await api.post('/foia/generate-redacted-pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob' // Expecting PDF binary data
  });
  
  return response;
};

// Set up axios interceptors for both API instances
const setupInterceptors = (apiInstance) => {
  // Add a request interceptor to include the auth token
  apiInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add a response interceptor to handle auth errors
  apiInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('authToken');
        // Redirect to login page
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Setup interceptors for both API instances
setupInterceptors(api);
setupInterceptors(legalApi);

// ==================== Query History APIs ====================

// Get list of queries with optional filtering
export const getQueryHistory = async (options = {}) => {
  const {
    query_type = null,
    status = null,
    limit = 50,
    offset = 0,
    user_id = 'default'
  } = options;

  const params = new URLSearchParams();
  if (query_type) params.append('query_type', query_type);
  if (status) params.append('status', status);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  params.append('user_id', user_id);

  const response = await api.get(`/queries?${params.toString()}`);
  return response.data;
};

// Get a specific query by ID
export const getQuery = async (queryId) => {
  const response = await api.get(`/queries/${queryId}`);
  return response.data;
};

// Create a new query (starts async processing)
export const createQuery = async (queryType, inputText, inputParams = {}, userId = 'default') => {
  const response = await api.post('/queries', {
    query_type: queryType,
    input_text: inputText,
    input_params: inputParams,
    user_id: userId
  });
  return response.data;
};

// Delete a query from history
export const deleteQuery = async (queryId) => {
  const response = await api.delete(`/queries/${queryId}`);
  return response.data;
};

// Get query status (for polling)
export const getQueryStatus = async (queryId) => {
  const response = await api.get(`/queries/${queryId}/status`);
  return response.data;
};

// Rerun a previous query
export const rerunQuery = async (queryId) => {
  const response = await api.post(`/queries/${queryId}/rerun`);
  return response.data;
};

// Export query history
export const exportQueryHistory = async (options = {}) => {
  const {
    query_type = null,
    status = null,
    user_id = null,
    limit = 1000
  } = options;

  const params = new URLSearchParams();
  if (query_type) params.append('query_type', query_type);
  if (status) params.append('status', status);
  if (user_id) params.append('user_id', user_id);
  params.append('limit', limit.toString());

  const response = await api.get(`/queries/export?${params.toString()}`);
  return response.data;
};

// Get query history stats
export const getQueryHistoryStats = async (userId = null) => {
  const params = userId ? `?user_id=${userId}` : '';
  const response = await api.get(`/queries/stats${params}`);
  return response.data;
};

// Cleanup old queries
export const cleanupQueryHistory = async (daysOld = 30, userId = 'default') => {
  const response = await api.post('/queries/cleanup', {
    days_old: daysOld,
    user_id: userId
  });
  return response.data;
};

// ==================== Legal Discovery Case APIs ====================

// Create a new discovery case
export const createDiscoveryCase = async (caseData) => {
  const response = await legalApi.post('/discovery/cases', caseData);
  return response.data;
};

// List all discovery cases
export const listDiscoveryCases = async () => {
  const response = await legalApi.get('/discovery/cases');
  return response.data;
};

// Get a specific case
export const getDiscoveryCase = async (caseId) => {
  const response = await legalApi.get(`/discovery/cases/${caseId}`);
  return response.data;
};

// Delete a case
export const deleteDiscoveryCase = async (caseId) => {
  const response = await legalApi.delete(`/discovery/cases/${caseId}`);
  return response.data;
};

// Add documents to a case
export const addCaseDocuments = async (caseId, documents) => {
  const response = await legalApi.post(`/discovery/cases/${caseId}/documents`, { documents });
  return response.data;
};

// Get documents with filters
export const getCaseDocuments = async (caseId, filters = {}) => {
  const response = await legalApi.get(`/discovery/cases/${caseId}/documents`, { params: filters });
  return response.data;
};

// Update a document's review status
export const updateCaseDocument = async (caseId, docId, updates) => {
  const response = await legalApi.put(`/discovery/cases/${caseId}/documents/${docId}`, updates);
  return response.data;
};

// Batch review multiple documents
export const batchReviewDocuments = async (caseId, decisions) => {
  const response = await legalApi.post(`/discovery/cases/${caseId}/documents/batch-review`, { decisions });
  return response.data;
};

// Run AI privilege analysis on all pending docs
export const analyzeCase = async (caseId) => {
  const response = await legalApi.post(`/discovery/cases/${caseId}/analyze`);
  return response.data;
};

// Run AI privilege analysis on a single doc
export const analyzeDocument = async (caseId, docId) => {
  const response = await legalApi.post(`/discovery/cases/${caseId}/documents/${docId}/analyze`);
  return response.data;
};

// Generate privilege log
export const generatePrivilegeLog = async (caseId, format = 'csv') => {
  const response = await legalApi.post(`/discovery/cases/${caseId}/privilege-log`, { output_format: format });
  return response.data;
};

// Assign Bates numbers
export const assignBatesNumbers = async (caseId, prefix = 'PROD', startNumber = 1) => {
  const response = await legalApi.post(`/discovery/cases/${caseId}/bates`, { prefix, start_number: startNumber });
  return response.data;
};

// Get production summary
export const getProductionSummary = async (caseId) => {
  const response = await legalApi.get(`/discovery/cases/${caseId}/production-summary`);
  return response.data;
};

// Export production set
export const exportProduction = async (caseId, options = {}) => {
  const response = await legalApi.post(`/discovery/cases/${caseId}/export`, null, { params: options });
  return response.data;
};

// Export the main API instance as default (for auth endpoints)
export default api;